// Policy pages content for the KHA storefront — 10 pages, 3 languages.
// Placeholders in square brackets like [support_email] are intentional: they
// mark facts the store owner must confirm (company details, exact timings,
// fees). Nothing unverified is stated as fact.

const P = {
  about: {
    en: {
      title: "About Us",
      body: [
        "KHA is an online marketplace for physical products, serving customers worldwide. We offer a curated catalog across categories such as electronics, home & living, fashion, beauty and accessories.",
        "Our model is simple: we source quality products at fair prices, inspect them before dispatch, and ship them to your door with tracking. Every order is protected by our buyer-protection promise.",
        "We work with vetted international suppliers and fulfillment partners so we can keep prices low without compromising on quality control.",
        "Have a question or feedback? We would love to hear from you — see our [contact_link|Contact Us] page.",
      ],
    },
    tr: {
      title: "Hakkımızda",
      body: [
        "KHA, dünya genelinde müşterilere hizmet veren fiziksel ürünler için bir çevrimiçi pazar yeridir. Elektronik, ev & yaşam, moda, güzellik ve aksesuar gibi kategorilerde özenle seçilmiş bir katalog sunuyoruz.",
        "Modelimiz basittir: kaliteli ürünleri uygun fiyatlarla tedarik eder, göndermeden önce kontrol eder ve takip numarasıyla kapınıza teslim ederiz. Her sipariş, alıcı koruma vaadimizle korunur.",
        "Fiyatları düşük tutarken kalite kontrolünden ödün vermemek için, denetlenmiş uluslararası tedarikçiler ve lojistik ortaklarıyla çalışıyoruz.",
        "Bir sorunuz ya da geri bildiriminiz mi var? [contact_link|Bize Ulaşın] sayfamızdan bize yazmaktan memnuniyet duyarız.",
      ],
    },
    ar: {
      title: "من نحن",
      body: [
        "KHA متجر إلكتروني للمنتجات الملموسة يخدم العملاء في جميع أنحاء العالم. نوفر تشكيلة منتقاة من الفئات مثل الإلكترونيات، المنزل والمعيشة، الأزياء، الجمال والإكسسوارات.",
        "فلسفتنا بسيطة: نجهز منتجات جودة بأسعار عادلة، نفحصها قبل الإرسال، ونشحنها حتى بابك مع رقم تتبع. كل طلب مشمول بوعد حماية المشتري لدينا.",
        "نعمل مع موردين وشركاء تنفيذ دوليين تم التحقق منهم، حتى نحافظ على الأسعار المنخفضة دون التنازل عن فحص الجودة.",
        "لديك سؤال أو ملاحظة؟ يسعدنا سماعك — راجع صفحة [contact_link|تواصل معنا].",
      ],
    },
  },

  contact: {
    en: {
      title: "Contact Us",
      body: [
        "We are here to help with orders, shipping, returns or anything else.",
        "**Email:** [support_email]",
        "**Support hours:** [support_hours]",
        "**Typical first response time:** [response_time]",
        "When contacting us about an order, please include your order number so we can help you faster.",
      ],
    },
    tr: {
      title: "Bize Ulaşın",
      body: [
        "Siparişler, kargo, iadeler veya başka her konuda yardımcı olmak için buradayız.",
        "**E-posta:** [support_email]",
        "**Destek saatleri:** [support_hours]",
        "**Ortalama ilk yanıt süresi:** [response_time]",
        "Bir sipariş hakkında yazarken, daha hızlı yardımcı olabilmemiz için lütfen sipariş numaranızı da ekleyin.",
      ],
    },
    ar: {
      title: "تواصل معنا",
      body: [
        "نحن هنا للمساعدة في الطلبات، الشحن، الاسترجاع أو أي شيء آخر.",
        "**البريد الإلكتروني:** [support_email]",
        "**ساعات الدعم:** [support_hours]",
        "**متوسط زمن أول رد:** [response_time]",
        "عند مراسلتنا بخصوص طلب، أضف رقم الطلب حتى نساعدك بشكل أسرع.",
      ],
    },
  },

  shipping: {
    en: {
      title: "Shipping & Delivery Policy",
      body: [
        "**Where we ship:** we ship physical products worldwide to supported countries. Available destinations for your address are shown at checkout.",
        "**Processing time:** orders are prepared for dispatch within [processing_time] after payment confirmation, excluding weekends and holidays.",
        "**Delivery time:** estimated delivery ranges are shown on the product page and at checkout based on your destination. These are estimates, not guarantees — actual times can vary due to customs, carrier load or local conditions.",
        "**Tracking:** every order ships with a tracking number. You can follow your parcel on the [track_link|Order Tracking] page or from your account.",
        "**Shipping fees:** fees (if any) and free-shipping thresholds are shown at checkout before you pay. Customs duties, import taxes or fees charged by your country are the recipient's responsibility unless stated otherwise at checkout.",
        "**Address accuracy:** please double-check your address. We ship to the address entered at checkout; corrections after dispatch may not be possible.",
        "**Split shipments:** items in one order may ship separately depending on stock location — you will never pay extra for this.",
      ],
    },
    tr: {
      title: "Kargo ve Teslimat Politikası",
      body: [
        "**Nereye gönderiyoruz:** fiziksel ürünleri dünyanın desteklenen ülkelerine gönderiyoruz. Adresiniz için geçerli ülkeler ödeme sayfasında gösterilir.",
        "**İşleme süresi:** siparişler, ödeme onayından sonra [processing_time] içinde (hafta sonu ve tatiller hariç) gönderime hazırlanır.",
        "**Teslim süresi:** tahmini teslim aralıkları, varış ülkenize göre ürün sayfasında ve ödeme sırasında gösterilir. Bunlar tahmindir, garanti değildir; gümrük, lojistik yoğunluğu veya yerel koşullar nedeniyle süreler değişebilir.",
        "**Takip numarası:** her sipariş takip numarasıyla gönderilir. Parselinizi [track_link|Sipariş Takibi] sayfasından veya hesabınızdan izleyebilirsiniz.",
        "**Kargo ücretleri:** ücretler (varsa) ve ücretsiz kargo eşikleri ödeme öncesinde gösterilir. Ülkeniz tarafından alınan gümrük vergileri ve ithalat masrafları, ödeme sırasında başka türlü belirtilmedikçe alıcıya aittir.",
        "**Adres doğruluğu:** lütfen adresinizi iki kez kontrol edin. Ödeme sırasında girilen adrese gönderim yapılır; gönderimden sonraki düzeltmeler mümkün olmayabilir.",
        "**Parçalı gönderi:** bir siparişteki ürünler stok konumuna göre ayrı ayrı gönderilebilir — bu durumda sizden ek ücret alınmaz.",
      ],
    },
    ar: {
      title: "سياسة الشحن والتوصيل",
      body: [
        "**أين نشحن:** نشحن المنتجات الملموسة لجميع الدول المدعومة حول العالم، والوجهات المتاحة لعنوانك تظهر عند الدفع.",
        "**مدة التجهيز:** يُجهز الطلب للإرسال خلال [processing_time] من تأكيد الدفع، باستثناء العطلات ونهاية الأسبوع.",
        "**مدة التوصيل:** تظهر التقديرات على صفحة المنتج وعند الدفع حسب وجهتك. هذه تقديرات وليست ضمانات — قد تختلف المدد الفعلية لأسباب جمركية أو حسب ضغط شركات الشحن أو الظروف المحلية.",
        "**التتبع:** كل طلب يُشحن برقم تتبع. يمكنك متابعة طرودك من صفحة [track_link|تتبع الطلب] أو من حسابك.",
        "**رسوم الشحن:** الرسوم (إن وجدت) وحدود الشحن المجاني تظهر قبل الدفع. الرسوم الجمركية وضرائب الاستيراد التي تفرضها دولتك على المستلم، ما لم يُذكر خلاف ذلك عند الدفع.",
        "**دقة العنوان:** تأكد من عنوانك. نشحن للعنوان المُدخل عند الدفع، وتعديله بعد الإرسال قد لا يكون ممكناً.",
        "**شحنات مقسمة:** قد تُشحن منتجات طلب واحد بشكل منفصل حسب موقع المخزون — ولن تتحمل أي رسوم إضافية لهذا.",
      ],
    },
  },

  returns: {
    en: {
      title: "Returns & Refund Policy",
      body: [
        "**Return window:** you may request a return within [return_window] of receiving your order. Some product categories (for example personal-care or intimate items) may be excluded for hygiene reasons — this is always stated on the product page.",
        "**Condition:** items should be unused and in their original packaging with all accessories included.",
        "**How to start a return:** contact us with your order number and the reason. We will guide you through the return steps and provide the return address.",
        "**Refunds:** once the returned item is received and inspected, we issue a refund to the original payment method within [refund_time]. Original shipping fees are refunded only when the return is due to our error (wrong, damaged or defective item).",
        "**Damaged or wrong item:** if your order arrives damaged, defective or incorrect, contact us within [damage_report_window] with photos of the item and packaging — we will arrange a replacement or refund, including return shipping where applicable.",
        "**Non-delivery:** if your parcel shows delivered but you did not receive it, or it has been stuck in transit for an extended period, contact us and we will investigate with the carrier.",
        "Statutory consumer rights in your country, where applicable, apply in addition to this policy.",
      ],
    },
    tr: {
      title: "İade ve Geri Ödeme Politikası",
      body: [
        "**İade süresi:** siparişinizi aldıktan sonra [return_window] içinde iade talebi oluşturabilirsiniz. Bazı ürün kategorileri (ör. kişisel bakım veya iç giyim) hijyen nedeniyle iade kapsamı dışında olabilir — bu her zaman ürün sayfasında belirtilir.",
        "**Ürün durumu:** ürünler kullanılmamış ve tüm aksesuarlarıyla orijinal ambalajında olmalıdır.",
        "**İade nasıl başlatılır:** sipariş numaranız ve sebebinizle bize yazın. İade adımlarında size yol gösterir ve iade adresini iletiriz.",
        "**Geri ödeme:** iade edilen ürün elimize ulaşip kontrol edildikten sonra [refund_time] içinde orijinal ödeme yöntemine iade yapılır. Kargo ücretleri yalnızca iade bizden kaynaklı hata (yanlış, hasarlı veya kusurlu ürün) ise iade edilir.",
        "**Hasarlı veya yanlış ürün:** siparişiniz hasarlı, kusurlu veya yanlış ulaştıysa [damage_report_window] içinde ürün ve ambalaj fotoğraflarıyla bize yazın — iade kargosu dahil değişim veya geri ödeme ayarlarız.",
        "**Teslim edilmeme:** kargo teslim edildi göstermesine rağmen size ulaşmadıysa veya uzun süredir yolda kaldıysa bize yazın, kargo şirketiyle soruştururuz.",
        "Ülkenizdeki yasal tüketici hakları, geçerli olduğu ölçüde bu politikanın yanında uygulanır.",
      ],
    },
    ar: {
      title: "سياسة الإرجاع والاستبدال",
      body: [
        "**مدة الإرجاع:** يمكنك طلب إرجاع خلال [return_window] من استلام طلبك. بعض الفئات (مثل منتجات العناية الشخصية) قد تُستثنى لأسباب صحية — ويُذكر ذلك دائماً على صفحة المنتج.",
        "**حالة المنتج:** يجب أن يكون المنتج غير مستخدم وبالعبة الأصلية مع كامل الملحقات.",
        "**كيف تبدأ الإرجاع:** راسلنا برقم الطلب وسبب الإرجاع، وسنرشدك للخطوات ونزودك بعنوان الإرجاع.",
        "**الاسترجاع المالي:** بعد وصول المنتج المعاد وفحصه، نعيد المبلغ لوسيلة الدفع الأصلية خلال [refund_time]. رسوم الشحن الأصلية تُعاد فقط إذا كان الإرجاع بسبب خطأ منا (منتج خاطئ أو تالف أو معيب).",
        "**منتج تالف أو خاطئ:** إذا وصلك الطلب تالفاً أو معيباً أو خاطئاً، راسلنا خلال [damage_report_window] مع صور المنتج والتغليف — ونرتب الاستبدال أو الاسترجاع المالي شاملاً شحن الإرجاع عند الاقتضاء.",
        "**عدم الوصول:** إذا ظهر الطلب «تم التوصيل» ولم يصلك، أو بقي في الطريق مدة طويلة بشكل غير معتاد، راسلنا وسنتابع مع شركة الشحن.",
        "تُطبق حقوق المستهلك القانونية في دولتك، حيثما تنطبق، إضافة إلى هذه السياسة.",
      ],
    },
  },

  cancellation: {
    en: {
      title: "Cancellation Policy",
      body: [
        "**Before dispatch:** you can cancel an order free of charge as long as it has not been dispatched yet. Contact us as early as possible with your order number.",
        "**After dispatch:** once an order has been handed to the carrier it can no longer be cancelled — you can instead refuse the delivery or use our [returns_link|Returns & Refund Policy].",
        "**Out-of-stock items:** if an item becomes unavailable after you order, we cancel that item (or the whole order) and refund it in full.",
        "**Refund timing for cancellations:** refunded amounts follow the timelines in our Returns & Refund Policy.",
      ],
    },
    tr: {
      title: "İptal Politikası",
      body: [
        "**Gönderim öncesi:** sipariş henüz gönderilmediyse ücretsiz olarak iptal edebilirsiniz. Mümkün olan en kısa sürede sipariş numaranızla bize yazın.",
        "**Gönderim sonrası:** sipariş kargoya verildikten sonra iptal edilemez — bunun yerine teslimatı reddedebilir veya [returns_link|İade ve Geri Ödeme Politikamızı] kullanabilirsiniz.",
        "**Stokta olmayan ürünler:** sipariş sonrası bir ürün tedarik edilemezse o ürünü (veya tüm siparişi) iptal eder ve ücretini tamamen iade ederiz.",
        "**İptal geri ödemeleri:** iade tutarları, İade ve Geri Ödeme Politikamızdaki süreleri takip eder.",
      ],
    },
    ar: {
      title: "سياسة الإلغاء",
      body: [
        "**قبل الإرسال:** يمكنك إلغاء طلبك مجاناً طالما لم يُشحن بعد. راسلنا بأسرع وقت مع رقم الطلب.",
        "**بعد الإرسال:** بعد تسليم الطلب لشركة الشحن لا يمكن إلغاؤه — يمكنك بدلاً من ذلك رفض الاستلام أو استخدام [returns_link|سياسة الإرجاع والاستبدال].",
        "**منتجات غير متوفرة:** إذا صار المنتج غير متوفر بعد طلبك، نلغاء المنتج (أو الطلب كاملاً) ونعيد المبلغ كاملاً.",
        "**توقيت استرجاع الإلغاء:** تتبع المبالغ المستردة نفس المدد المذكورة في سياسة الإرجاع والاستبدال.",
      ],
    },
  },

  terms: {
    en: {
      title: "Terms & Conditions",
      body: [
        "By using this website and placing an order you agree to these terms. If you do not agree, please do not use the site.",
        "**Accounts:** you are responsible for keeping your account credentials confidential and for activity under your account. You must provide accurate information.",
        "**Orders:** your order is an offer to buy. We accept it when we confirm the order (usually by email). In the rare case of a pricing or availability error, we may cancel the affected item and refund it.",
        "**Prices & currency:** prices are shown in your selected currency and include applicable taxes where stated. Currency conversion may be handled by your payment provider.",
        "**Product descriptions:** we describe products as accurately as possible; slight differences in color or packaging may occur. Product images are illustrative.",
        "**Intellectual property:** site content, design and branding belong to KHA and may not be copied without permission.",
        "**Liability:** to the maximum extent permitted by law, our liability for any order is limited to the amount you paid for that order.",
        "**Changes:** we may update these terms; the version in force is the one published when you place your order.",
        "**Governing law:** [governing_law]",
      ],
    },
    tr: {
      title: "Şartlar ve Koşullar",
      body: [
        "Bu web sitesini kullanarak ve sipariş vererek bu şartları kabul etmiş olursunuz. Kabul etmiyorsanız lütfen siteyi kullanmayın.",
        "**Hesaplar:** hesap bilgilerinizin gizliliğinden ve hesabınız altındaki işlemlerden siz sorumlusunuz. Doğru bilgi vermelisiniz.",
        "**Siparişler:** siparişiniz bir satın alma teklifidir. Siparişi onayladığımızda (genellikle e-posta ile) kabul edilmiş olur. Nadiren görülen fiyat veya stok hatasında, etkilenen ürünü iptal edip ücretini iade edebiliriz.",
        "**Fiyatlar ve para birimi:** fiyatlar seçtiğiniz para biriminde ve belirtilen yerlerde geçerli vergilerle gösterilir. Döviz dönüşümü ödeme sağlayıcınız tarafından yapılabilir.",
        "**Ürün tanımları:** ürünleri olabildiğince doğru tanımlarız; renk veya ambalajda küçük farklılıklar olabilir. Ürün görselleri temsilidir.",
        "**Fikri mülkiyet:** site içeriği, tasarımı ve markası KHA'ya aittir; izinsiz kopyalanamaz.",
        "**Sorumluluk:** yasaların izin verdiği azami ölçüde, herhangi bir sipariş için sorumluluğumuz o sipariş için ödediğiniz tutarla sınırlıdır.",
        "**Değişiklikler:** bu şartları güncelleyebiliriz; geçerli olan, sipariş verdiğiniz anda yayında olan sürümdür.",
        "**Uygulanacak hukuk:** [governing_law]",
      ],
    },
    ar: {
      title: "الشروط والأحكام",
      body: [
        "باستخدامك هذا الموقع وإتمامك للطلب فأنت توافق على هذه الشروط. إن لم توافق، فضلاً لا تستخدم الموقع.",
        "**الحسابات:** أنت مسؤول عن الحفاظ على سرية بيانات دخولك وعن أي نشاط يتم عبر حسابك، وعن تقديم معلومات صحيحة.",
        "**الطلبات:** طلبك يُعد عرضاً للشراء، ونحن نقبله عند تأكيد الطلب (عادةً بالبريد الإلكتروني). في حالات نادرة من خطأ السعر أو التوفر، يجوز إلغاء المنتج المتأثر وإعادة مبلغه.",
        "**الأسعار والعملة:** تُعرض الأسعار بالعملة التي اخترتها وتشمل الضرائب المنطبقة حيث يُذكر ذلك. تحويل العملات قد تتم من جهة الدفع لديك.",
        "**وصف المنتجات:** نصف المنتجات بأقصى دقة ممكنة، لكن قد توجد فروق بسيطة في اللون أو التغليف، والصور توضيحية.",
        "**الملكية الفكرية:** محتوى الموقع وتصميمه وعلامته التجارية ملك لـKHA ولا يجوز نسخها بدون إذن.",
        "**المسؤولية:** إلى الحد الأقصى الذي يسمح به القانون، مسؤوليتنا عن أي طلب محدودة بمبلغ ما دفعته عنه.",
        "**التعديلات:** يجوز تحديث هذه الشروط، والنسخة السارية هي المنشورة وقت إتمام طلبك.",
        "**القانون الحاكم:** [governing_law]",
      ],
    },
  },

  privacy: {
    en: {
      title: "Privacy Policy",
      body: [
        "We collect only the data needed to run your orders and improve your experience.",
        "**What we collect:** your name, email, addresses, phone, order history, and technical data (device, browser, pages visited) for security and analytics.",
        "**Why:** to create and fulfill your orders, provide support, prevent fraud, and (with your consent) send marketing emails. We do not sell your personal data.",
        "**Payments:** payments are processed by certified third-party providers. We never see or store your full card details.",
        "**Sharing:** we share the minimum necessary data with carriers (to deliver) and payment/anti-fraud providers. Fulfillment partners receive only what is required to ship your order.",
        "**Retention:** account and order data are kept while your account is active and as required by tax/commercial law.",
        "**Your rights:** you can access, correct or delete your personal data and object to marketing at any time — contact us via [contact_link|Contact Us].",
        "**Cookies:** see our [cookies_link|Cookie Policy].",
      ],
    },
    tr: {
      title: "Gizlilik Politikası",
      body: [
        "Siparişlerinizi yürütmek ve deneyiminizi geliştirmek için gereken verileri toplarız, fazlasını değil.",
        "**Topladığımız veriler:** adınız, e-posta, adresleriniz, telefon, sipariş geçmişiniz ve güvenlik/analiz için teknik veriler (cihaz, tarayıcı, görüntülenen sayfalar).",
        "**Amaç:** siparişlerinizi oluşturmak ve teslim etmek, destek vermek, dolandırıcılığı önlemek ve (onayınızla) pazarlama e-postaları göndermek. Kişisel verilerinizi satmayız.",
        "**Ödemeler:** ödemeler sertifikalı üçüncü taraf sağlayıcılar tarafından işlenir. Kart bilgilerinizin tamamını asla görmeyiz veya saklamayız.",
        "**Paylaşım:** yalnızca gerekli asgari veriyi kargo şirketleriyle (teslimat için) ve ödeme/dolandırıcılık önleme sağlayıcılarıyla paylaşırız. Lojistik ortaklarımıza yalnızca siparişinizi gönderebilmek için gereken bilgiler iletilir.",
        "**Saklama:** hesap ve sipariş verileri, hesabınız aktif olduğu sürece ve vergi/ticari mevzuatın gerektirdiği süre boyunca saklanır.",
        "**Haklarınız:** kişisel verilerinize erişme, düzeltme, silme ve pazarlamaya itiraz etme hakkınız her zaman vardır — [contact_link|Bize Ulaşın] sayfasından yazın.",
        "**Çerezler:** bkz. [cookies_link|Çerez Politikası].",
      ],
    },
    ar: {
      title: "سياسة الخصوصية",
      body: [
        "نجمع فقط البيانات اللازمة لتنفيذ طلباتك وتحسين تجربتك.",
        "**ما نجمعه:** الاسم، البريد الإلكتروني، العناوين، الهاتف، سجل الطلبات، وبيانات تقنية (الجهاز، المتصفح، الصفحات التي زرتها) لأغراض الأمان والتحليل.",
        "**الغرض:** إنشاء طلباتك وتنفيذها، تقديم الدعم، منع الاحتيال، وإرسال رسائل تسويقية بموافقتك. نحن لا نبيع بياناتك الشخصية.",
        "**الدفع:** تتم معالجة المدفوعات عبر مزودين معتمدين من أطراف ثالثة، ونحن لا نرى بيانات بطاقتك الكاملة ولا نخزنها إطلاقاً.",
        "**المشاركة:** نشارك الحد الأدنى الضروري من البيانات مع شركات الشحن (للتوصيل) ومزودي الدفع ومكافحة الاحتيال. شركاء التنفيذ يتلقون فقط ما يلزم لشحن طلبك.",
        "**مدة الحفظ:** تُحفظ بيانات الحساب والطلبات طوال نشاط حسابك والمدد التي يفرضها القانون الضريبي/التجاري.",
        "**حقوقك:** يمكنك الوصول لبياناتك وتصحيحها أو حذفها ورفض التسويق في أي وقت — عبر صفحة [contact_link|تواصل معنا].",
        "**ملفات الارتباط:** راجع [cookies_link|سياسة ملفات الارتباط].",
      ],
    },
  },

  cookies: {
    en: {
      title: "Cookie Policy",
      body: [
        "**Essential cookies:** required for the cart, checkout, login session and security. The site cannot function properly without them.",
        "**Preference cookies:** remember your language, currency and region choices.",
        "**Analytics cookies (with consent):** help us understand how the site is used so we can improve it.",
        "**Marketing cookies (with consent):** used to measure campaigns and show relevant offers.",
        "**Managing cookies:** you can block or delete cookies in your browser settings; blocking essential cookies will break cart and checkout functionality.",
      ],
    },
    tr: {
      title: "Çerez Politikası",
      body: [
        "**Zorunlu çerezler:** sepet, ödeme, oturum ve güvenlik için gereklidir. Bunlar olmadan site düzgün çalışmaz.",
        "**Tercih çerezleri:** dil, para birimi ve bölge seçimlerinizi hatırlar.",
        "**Analitik çerezleri (onayla):** site kullanımını anlayıp geliştirmemizi sağlar.",
        "**Pazarlama çerezleri (onayla):** kampanyaları ölçmek ve ilgili teklifler göstermek için kullanılır.",
        "**Çerezleri yönetme:** tarayıcı ayarlarınızdan çerezleri engelleyebilir veya silebilirsiniz; zorunlu çerezleri engellemek sepet ve ödeme işlevlerini bozar.",
      ],
    },
    ar: {
      title: "سياسة ملفات الارتباط (الكوكيز)",
      body: [
        "**الكوكيز الأساسية:** ضرورية لعمل السلة والدفع وجلسة الدخول والأمان. الموقع لا يعمل بشكل صحيح بدونها.",
        "**كوكيز التفضيلات:** تحفظ اختياراتك للغة والعملة والمنطقة.",
        "**كوكيز التحليلات (بموافقتك):** تساعدنا في فهم استخدام الموقع لتحسينه.",
        "**كوكيز التسويق (بموافقتك):** لقياس الحملات وعرض العروض المناسبة.",
        "**إدارة الكوكيز:** يمكنك منعها أو حذفها من إعدادات المتصفح؛ منع الكوكيز الأساسية يعطل السلة والدفع.",
      ],
    },
  },

  faq: {
    en: {
      title: "FAQ",
      body: [
        "**How do I track my order?** Open [track_link|Order Tracking] or go to your account → orders. You will need your order number; logged-in users see live status automatically.",
        "**How long does delivery take?** Estimates per destination are shown on the product page and at checkout. They depend on your country and the item's stock location.",
        "**Can I change my address after ordering?** Contact us immediately — if the order has not been dispatched yet we can update it.",
        "**Are customs fees included?** Unless stated otherwise at checkout, customs duties or import taxes charged by your country are not included.",
        "**How do I return an item?** See the [returns_link|Returns & Refund Policy] — the window and conditions are described there.",
        "**Is my payment secure?** Yes. Payments are handled by certified providers and your card data never reaches us.",
        "**Do I need an account to order?** No, guest checkout is available — but an account lets you follow all orders and save addresses.",
      ],
    },
    tr: {
      title: "Sıkça Sorulan Sorular",
      body: [
        "**Siparişimi nasıl takip ederim?** [track_link|Sipariş Takibi] sayfasını açın veya hesabınız → siparişler bölümüne gidin. Sipariş numarası gerekir; giriş yapan kullanıcılar durumu otomatik görür.",
        "**Teslimat ne kadar sürer?** Ülkenize göre tahmini süreler ürün sayfasında ve ödeme sırasında görünür. Ülkenize ve ürünün stok konumuna bağlıdır.",
        "**Siparişten sonra adresimi değiştirebilir miyim?** Hemen bize yazın — sipariş henüz gönderilmediyse güncelleyebiliriz.",
        "**Gümrük ücretleri dahil mi?** Ödeme sırasında başka türlü belirtilmedikçe, ülkenizin gümrük vergileri dahil değildir.",
        "**Nasıl iade ederim?** [returns_link|İade ve Geri Ödeme Politikası]na bakın — süre ve koşullar orada yazılıdır.",
        "**Ödemem güvenli mi?** Evet. Ödemeler sertifikalı sağlayıcılar üzerinden işlenir, kart bilgileriniz bize ulaşmaz.",
        "**Sipariş için hesap şart mı?** Hayır, misafir alışveriş yapılabilir — ancak hesapla tüm siparişlerinizi takip edip adreslerinizi kaydedebilirsiniz.",
      ],
    },
    ar: {
      title: "الأسئلة الشائعة",
      body: [
        "**كيف أتتبع طلبي؟** افتح صفحة [track_link|تتبع الطلب] أو حسابك ← الطلبات. ستحتاج رقم الطلب؛ والمسجلون يرون الحالة مباشرة.",
        "**كم يستغرق التوصيل؟** التقديرات حسب دولتك تظهر على صفحة المنتج وعند الدفع، وتعتمد على بلدك وموقع مخزون المنتج.",
        "**هل أستطيع تعديل العنوان بعد الطلب؟** راسلنا فوراً — إذا لم يُشحن الطلب بعد نستطيع تعديله.",
        "**هل الرسوم الجمركية مشمولة؟** ما لم يُذكر خلاف ذلك عند الدفع، فالرسوم الجمركية التي تفرضها دولتك غير مشمولة.",
        "**كيف أرجع منتجاً؟** راجع [returns_link|سياسة الإرجاع والاستبدال] — المدة والشروط موصوفة هناك.",
        "**هل الدفع آمن؟** نعم. المدفوعات تمر عبر مزودين معتمدين وبيانات بطاقتك لا تصل إلينا أبداً.",
        "**هل أحتاج حساباً للطلب؟** لا، الشراء كزائر متاح — لكن الحساب يتيح متابعة كل طلباتك وحفظ عناوينك.",
      ],
    },
  },

  order_tracking: {
    en: {
      title: "Order Tracking",
      body: [
        "**Logged-in customers:** open your account → [orders_link|My Orders] to see the live status of every order, from receipt through delivery.",
        "**Guest orders:** use the [track_link|tracking page] with your order number.",
        "**What the statuses mean:**",
        "- *Order received* — we got your order and payment.",
        "- *Processing* — your item is being prepared.",
        "- *Purchased from supplier* — sourced from the supplier.",
        "- *Preparing shipment* — packed and awaiting the carrier.",
        "- *Shipped / In transit* — on the way; tracking updates as the parcel moves.",
        "- *Delivered* — the parcel reached its destination.",
        "- *Cancelled / Refunded* — the order will not arrive / your money is being returned.",
        "Statuses update automatically as the carrier reports movement. If a status has not changed for an unusually long time, contact us.",
      ],
    },
    tr: {
      title: "Sipariş Takibi",
      body: [
        "**Kayıtlı müşteriler:** hesabınız → [orders_link|Siparişlerim] bölümünden her siparişin güncel durumunu, alınmasından teslimata kadar görebilirsiniz.",
        "**Misafir siparişleri:** sipariş numaranızla [track_link|takip sayfasını] kullanın.",
        "**Durumlar ne anlama gelir:**",
        "- *Sipariş alındı* — siparişiniz ve ödemeniz bize ulaştı.",
        "- *İşleniyor* — ürününüz hazırlanıyor.",
        "- *Tedarikçiye sipariş verildi* — tedarikçiden tedarik edildi.",
        "- *Gönderime hazırlanıyor* — paketlendi, kargoyu bekliyor.",
        "- *Kargolandı / Yolda* — yolda; paket hareket ettikçe takip güncellenir.",
        "- *Teslim edildi* — paket varış noktasına ulaştı.",
        "- *İptal / İade edildi* — sipariş gelmeyecek / ücretiniz iade ediliyor.",
        "Durumlar, kargo şirketi bildirdikçe otomatik güncellenir. Bir durum alışılmadık kadar uzun süre değişmediyse bize yazın.",
      ],
    },
    ar: {
      title: "تتبع الطلب",
      body: [
        "**العملاء المسجلون:** من حسابك ← [orders_link|طلباتي] ترى الحالة الحية لكل طلب، من الاستلام حتى التوصيل.",
        "**طلبات الزوار:** استخدم صفحة [track_link|التتبع] مع رقم طلبك.",
        "**معنى الحالات:**",
        "- *تم استلام الطلب* — وصلنا طلبك ودفعك.",
        "- *قيد المعالجة* — يتم تجهيز منتجك.",
        "- *تم الشراء من المورد* — جُلب من المورد.",
        "- *تجهيز الشحنة* — مُعبأ وبانتظار شركة الشحن.",
        "- *شُحن / في الطريق* — في الطريق؛ والتتبع يتحدث مع حركة الطرد.",
        "- *تم التوصيل* — وصل الطرد إلى وجهته.",
        "- *ملغي / مسترجع مالياً* — لن يصلك الطلب / يُعاد مبلغك.",
        "تتحدث الحالات تلقائياً حسب تقارير شركة الشحن. إذا ثابتة الحالة مدة غير معتادة، راسلنا.",
      ],
    },
  },
};

export const POLICY_ORDER = [
  ["about", "about_us"],
  ["contact", "contact_us"],
  ["shipping", "shipping_policy"],
  ["returns", "returns_policy"],
  ["cancellation", "cancellation_policy"],
  ["terms", "terms_conditions"],
  ["privacy", "privacy_policy"],
  ["cookies", "cookie_policy"],
  ["faq", "faq"],
  ["order_tracking", "order_tracking"],
];

export const POLICIES = P;
